import CloseIcon from "@/assets/colorable/close_x.svg?react";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import clsxm from "@/lib/clsxm";
import {ModalContext} from "@/store/modalContext";
import last from "lodash/last";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
} from "react";
import {useTranslation} from "react-i18next";
import {Portal} from "react-portal";
import {useClickAway, useKeyPressEvent, useLockBodyScroll} from "react-use";

const Modal: FC<
  PropsWithChildren & {
    ref?: RefObject<HTMLDivElement>;
    onClose(): void;
    onNegativeButtonClick?(): void;
    modalTitle?: string;
    onConfirm?(): void;
    confirmButtonText?: string;
    customBottomRow?: ReactNode;
    customTitleRow?: ReactNode;
    negativeButtonText?: string;
    backgroundClassName?: string;
    className?: string;
    containerClassName?: string;
    isConfirmDisabled?: boolean;
    isLoading?: boolean;
    shouldCloseOnConfirm?: boolean;
    shouldHideCloseButton?: boolean;
    shouldHideModalTitle?: boolean;
    shouldLockBodyScroll?: boolean;
    style?: CSSProperties;
  }
> = ({
  ref,
  children,
  onClose,
  modalTitle,
  onConfirm,
  confirmButtonText,
  customBottomRow,
  negativeButtonText,
  onNegativeButtonClick,
  backgroundClassName,
  className,
  containerClassName,
  customTitleRow,
  isConfirmDisabled = false,
  isLoading = false,
  shouldCloseOnConfirm = true,
  shouldHideCloseButton = false,
  shouldHideModalTitle = false,
  shouldLockBodyScroll = true,
  style,
}) => {
  useLockBodyScroll(shouldLockBodyScroll);
  const id = useId();
  const {t} = useTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    if (!isLoading && !shouldHideCloseButton) onClose();
  }, [onClose, isLoading, shouldHideCloseButton]);
  const {modalIds, setModalId, deleteModalId} = useContext(ModalContext);
  useEffect(() => {
    setModalId(id);

    return () => {
      deleteModalId(id);
    };
  }, [id]);
  const isLastModal = useMemo(() => last(modalIds) === id, [modalIds, id]);
  useClickAway(divRef, () => {
    if (isLastModal) close();
  }, ["mousedown"]);
  useKeyPressEvent("Escape", () => {
    if (isLastModal) close();
  });

  return (
    <Portal node={document && document.getElementById("root")}>
      <div
        style={{
          zIndex: 9999 + modalIds.findIndex(modalId => modalId === id),
        }}
        className={clsxm(
          "font-roboto fixed top-1/2 left-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-black/70",
          containerClassName,
        )}>
        <div
          style={style}
          className={clsxm(
            "border-neutral-white-dark flex h-auto max-h-[70vh] w-[80vw] flex-col overflow-auto rounded-md border bg-white shadow-lg md:max-h-[95vh] md:w-4/6",
            backgroundClassName,
          )}
          ref={divRef}>
          <div ref={ref}>
            {!shouldHideModalTitle && (
              <div className="border-neutral-white-dark flex flex-row items-center justify-between border-b-1">
                {customTitleRow ?? <span className="text-neutral-black p-5 text-xl font-semibold">{modalTitle}</span>}
                {!shouldHideCloseButton && (
                  <button onClick={close} className="mr-5 h-fit" disabled={isLoading}>
                    <CloseIcon className="text-neutral-grey-light hover:text-neutral-grey-dark h-5 w-5 transition-colors duration-150" />
                  </button>
                )}
              </div>
            )}
            <div className={clsxm("flex flex-col gap-5 p-5", className)}>{children}</div>
            {customBottomRow ?? (
              <div className="border-neutral-white-dark flex shrink-0 grow gap-3 border-t-1 p-5">
                <Button
                  buttonStyle="White"
                  className="flex-1"
                  isDisabled={isLoading}
                  onClick={onNegativeButtonClick ?? close}>
                  {negativeButtonText ?? t("Cancel")}
                </Button>
                <Button
                  isDisabled={isConfirmDisabled || isLoading}
                  onClick={() => {
                    onConfirm!();
                    if (shouldCloseOnConfirm) close();
                  }}
                  buttonStyle="Gradient"
                  className="flex-1">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <span className="z-10 font-medium md:text-lg">{confirmButtonText ?? t("confirm")}</span>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default memo(Modal);
